import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { toast } from "sonner";
import { FileText, ChevronLeft, Copy, Check, Loader, Flag } from "lucide-react";

const GROQ_KEY = "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";

async function generateBrief(data) {
  const { board, steps, commitments, decisions } = data;
  const completed = steps.filter(s => s.completed_at);
  const pending = steps.filter(s => !s.completed_at && !s.locked);
  const locked = steps.filter(s => !s.completed_at && s.locked);

  const prompt = `Generate a professional handoff brief for the following project. Be concise and structured.

PROJECT: ${board.name}
${board.description ? `Description: ${board.description}` : ""}

PROGRESS: ${completed.length}/${steps.length} steps complete (${Math.round(completed.length/steps.length*100) || 0}%)

COMPLETED STEPS:
${completed.map(s => `- ${s.title}${s.proof ? ` [Proof: ${s.proof}]` : ""}`).join("\n") || "None yet"}

CURRENT / PENDING:
${pending.map(s => `- ${s.title}${s.due_date ? ` (due ${s.due_date.slice(0,10)})` : ""}`).join("\n") || "None"}

LOCKED (waiting on dependencies):
${locked.map(s => `- ${s.title}`).join("\n") || "None"}

RELATED COMMITMENTS:
${commitments.length > 0 ? commitments.map(c => `- [${c.status.toUpperCase()}] ${c.title}${c.due_date ? ` — due ${c.due_date.slice(0,10)}` : ""}${c.parties?.length ? ` (with ${c.parties.join(", ")})` : ""}`).join("\n") : "None"}

DECISIONS MADE:
${decisions.length > 0 ? decisions.filter(d => d.status === "decided").map(d => { const chosen = d.options?.find(o => o.option_id === d.chosen_option_id); return `- ${d.title}: chose "${chosen?.title || "unknown"}"${d.reasoning ? ` — ${d.reasoning}` : ""}`; }).join("\n") : "None"}

Write a handoff brief with these sections:
1. **Project Overview** — 2-3 sentence summary of what this is and where it stands
2. **What's Been Done** — key completed milestones
3. **Current State** — what's actively in progress right now
4. **What's Next** — the next 3 actions needed
5. **Outstanding Commitments** — any promises that need to be honoured
6. **Key Decisions Made** — important choices and their reasoning
7. **Handoff Notes** — anything someone picking this up needs to know

Keep it tight, professional, and actionable.`;

  const res = await fetch("https://api.groq.com/openai/v1/chat/completions", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${GROQ_KEY}` },
    body: JSON.stringify({
      model: "llama-3.3-70b-versatile",
      messages: [{ role: "user", content: prompt }],
      temperature: 0.4,
      max_tokens: 1200,
    }),
  });
  const json = await res.json();
  return json.choices?.[0]?.message?.content || "Failed to generate brief.";
}

export default function HandoffBrief({ embedded = false }) {
  const { current } = useWorkspace();
  const navigate = useNavigate();
  const wsId = current?.workspace_id;

  const [boards, setBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [brief, setBrief] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/boards`).then(({ data }) => setBoards(data)).catch(() => {});
  }, [wsId]);

  const generate = async (board) => {
    if (!wsId || !board) return;
    setLoading(true); setBrief("");
    try {
      const [stepsRes, commsRes, decisionsRes] = await Promise.all([
        api.get(`/workspaces/${wsId}/boards/${board.board_id}/steps`),
        api.get(`/workspaces/${wsId}/commitments`),
        api.get(`/workspaces/${wsId}/decisions`),
      ]);
      const text = await generateBrief({
        board,
        steps: stepsRes.data || [],
        commitments: commsRes.data || [],
        decisions: decisionsRes.data || [],
      });
      setBrief(text);
    } catch (err) {
      toast.error("Failed to generate brief");
      setBrief("");
    } finally { setLoading(false); }
  };

  const handleSelect = (board) => {
    setSelectedBoard(board);
    generate(board);
  };

  const copyBrief = () => {
    navigator.clipboard.writeText(brief).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Format markdown-ish text with bold headers
  const formatBrief = (text) => {
    return text.split("\n").map((line, i) => {
      if (line.startsWith("**") && line.includes("**", 2)) {
        const title = line.replace(/\*\*/g, "");
        return <p key={i} className="text-[13px] font-semibold text-[#EDEDED] mt-4 mb-1">{title}</p>;
      }
      if (line.startsWith("- ")) return <p key={i} className="text-[12px] text-[#A1A1AA] pl-4 mb-0.5">• {line.slice(2)}</p>;
      if (line.trim() === "") return <div key={i} className="h-1" />;
      return <p key={i} className="text-[12px] text-[#A1A1AA] leading-relaxed mb-1">{line}</p>;
    });
  };

  return (
    <div className="flex h-full bg-[#0A0A0A]">
      {/* Board list */}
      <div className="w-64 flex-shrink-0 border-r border-[#1A1A1A] flex flex-col">
        <div className="px-4 py-4 border-b border-[#1A1A1A]">
          {!embedded && (
            <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[10px] text-[#71717A] hover:text-[#A1A1AA] transition-colors mb-2">
              <ChevronLeft className="w-3 h-3" /> Resources
            </button>
          )}
          <h2 className="text-[13px] font-semibold text-[#EDEDED]">Handoff Briefs</h2>
          <p className="text-[10px] text-[#71717A] mt-0.5">Select a project to generate a brief</p>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          {boards.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <Flag className="w-6 h-6 text-[#2A2A2A] mx-auto mb-2" />
              <p className="text-[11px] text-[#71717A]">No boards yet</p>
              <button onClick={() => navigate("/boards")} className="text-[10px] text-[#5A90F5] mt-1 hover:underline">Create a board →</button>
            </div>
          ) : boards.map(b => {
            const pct = b.step_count > 0 ? Math.round(b.completed_count / b.step_count * 100) : 0;
            const isActive = selectedBoard?.board_id === b.board_id;
            return (
              <button key={b.board_id} onClick={() => handleSelect(b)}
                className={`w-full text-left px-4 py-3 hover:bg-[#0F0F0F] transition-colors ${isActive ? "bg-[#141414]" : ""}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  {b.emoji && <span className="text-sm">{b.emoji}</span>}
                  <span className="text-[11px] font-medium truncate flex-1" style={{ color: isActive ? "#EDEDED" : "#A1A1AA" }}>{b.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-0.5 bg-[#1A1A1A] rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: b.color || "#5A90F5" }} />
                  </div>
                  <span className="text-[9px] text-[#71717A] font-mono">{pct}%</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Brief content */}
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {!selectedBoard ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-[#111] border border-[#2A2A2A] flex items-center justify-center">
              <FileText className="w-7 h-7 text-[#71717A]" />
            </div>
            <div className="text-center">
              <p className="text-[15px] font-semibold text-[#71717A]">Select a project</p>
              <p className="text-[12px] text-[#52525B] mt-1 max-w-xs">Choose a progress board on the left to generate an AI handoff brief</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="px-8 py-4 border-b border-[#1A1A1A] flex items-center justify-between flex-shrink-0">
              <div>
                <h1 className="text-[16px] font-bold text-[#EDEDED]">{selectedBoard.name}</h1>
                <p className="text-[11px] text-[#71717A]">Handoff brief · AI generated</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => generate(selectedBoard)} disabled={loading}
                  className="px-3 py-1.5 rounded-xl text-[11px] border border-[#2A2A2A] text-[#A1A1AA] hover:text-[#EDEDED] hover:border-[#3A3A3A] disabled:opacity-40 transition-colors">
                  Regenerate
                </button>
                {brief && (
                  <button onClick={copyBrief} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] bg-[#5A90F5] hover:bg-[#4A80E5] text-white transition-colors">
                    {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                  </button>
                )}
              </div>
            </div>

            {/* Brief body */}
            <div className="flex-1 overflow-y-auto px-8 py-6">
              {loading ? (
                <div className="flex flex-col items-center justify-center h-48 gap-3">
                  <Loader className="w-6 h-6 text-[#5A90F5] animate-spin" />
                  <p className="text-[12px] text-[#71717A]">Generating brief…</p>
                </div>
              ) : brief ? (
                <div className="max-w-2xl">
                  <div className="p-6 bg-[#111] border border-[#2A2A2A] rounded-2xl">
                    {formatBrief(brief)}
                  </div>
                </div>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
